import {
  Button,
  ButtonGroup,
  FormGroup,
  Input,
  Label,
  Select,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/date'
import commonUtils from '@/utils/commonUtils'
import { tableStyles } from '@/styles/dataTable/style'
import { useRedis } from '@/hooks/useRedis'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface DataRow {
  noticeSn: number
  noticeSj: string
  tpNoticeYn: string
  noticeFromDate: string
  noticeToDate: string
  readngCnt: number
  popupYn: string
  useYn: string
  openYn: string
  regId: string
  updId: string
  regDate: string
  updDate: string
}

const NoticeMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<DataRow[]>([])
  const { getComCodes, getComCodeValue } = useRedis()

  const [srchData, setSrchData] = useState(
    location.state?.srchData || {
      srchOpt: '00',
      srchStr: '',
      openYn: '',
      useYn: '',
      tpNoticeYn: '',
      popupYn: '',
    },
  )
  const [currentPage, setCurrentPage] = useState(location.state?.page || 1)
  const [perPage, setPerPage] = useState(location.state?.perPage || 10)

  const columns: TableColumn<DataRow>[] = [
    {
      name: 'No.',
      selector: (row) => row.noticeSn,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.noticeSn}</div>
      ),
    },
    {
      name: '공지기간',
      selector: (row) =>
        `${commonUtils.formatDate(row.noticeFromDate, false)} ~ ${commonUtils.formatDate(row.noticeToDate, false)}`,
      sortable: true,
      center: true,
      wrap: true,
      width: '15%',
    },
    {
      name: '제목',
      selector: (row) => row.noticeSj,
      sortable: true,
      width: '20%',
    },
    {
      name: '공개여부',
      cell: (row) => getComCodeValue('OPEN_YN', row.openYn)?.codeNm,
      selector: (row) => row.openYn,
      sortable: true,
      center: true,
      wrap: true,
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.openYn === 'Y',
          style: {
            color: 'red',
          },
        },
      ],
    },
    {
      name: '사용여부',
      cell: (row) => getComCodeValue('USE_YN', row.useYn)?.codeNm,
      selector: (row) => row.useYn,
      sortable: true,
      center: true,
      wrap: true,
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.useYn === 'Y',
          style: {
            color: 'red',
          },
        },
      ],
    },
    {
      name: '상단고정',
      cell: (row) => getComCodeValue('TP_NOTICE_YN', row.tpNoticeYn)?.codeNm,
      selector: (row) => row.tpNoticeYn,
      sortable: true,
      center: true,
      wrap: true,
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.tpNoticeYn === 'Y',
          style: {
            color: 'red',
          },
        },
      ],
    },
    {
      name: '팝업여부',
      cell: (row) => getComCodeValue('POPUP_YN', row.popupYn)?.codeNm,
      selector: (row) => row.popupYn,
      sortable: true,
      center: true,
      wrap: true,
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.popupYn === 'Y',
          style: {
            color: 'red',
          },
        },
      ],
    },
    {
      name: '조회수',
      selector: (row) => row.readngCnt,
      sortable: true,
      wrap: true,
      width: '80px',
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.readngCnt}</div>
      ),
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '수정일',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate, false),
      sortable: true,
      wrap: true,
    },

    {
      name: '',
      cell: (row) => (
        <>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.noticeSn)}
          >
            수정
          </Button>
        </>
      ),
      sortable: false,
    },
  ]

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement // 타입 캐스팅
    setSrchData((prev: any) => ({ ...prev, [name]: value }))
  }

  const handleButtonUpdateClick = async (noticeSn: number) => {
    navigate(`/noticeMgmt/detail`, {
      state: {
        noticeSn: noticeSn,
        srchData: srchData,
        page: currentPage,
        perPage: perPage,
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getNoticeList()
    }
  }

  const getNoticeList = async () => {
    try {
      // const params: any = {}
      // // 검색 조건이 있을 때만 params에 추가
      // if (srchData.srchOpt.trim()) {
      //   params.srchOpt = srchData.srchOpt
      // }
      // if (srchData.srchStr.trim()) {
      //   params.srchStr = srchData.srchStr
      // }
      // if (srchData.tpNoticeYn.trim()) {
      //   params.tpNoticeYn = srchData.tpNoticeYn
      // }
      // if (srchData.openYn.trim()) {
      //   params.openYn = srchData.openYn
      // }
      // if (srchData.useYn.trim()) {
      //   params.useYn = srchData.useYn
      // }
      // if (srchData.popYn.trim()) {
      //   params.popYn = srchData.popYn
      // }

      // 값이 있는 경우만 필터링
      const params = Object.fromEntries(
        Object.entries(srchData).filter(
          ([_, value]) => typeof value === 'string' && value.trim(),
        ),
      )

      const response = await axios.get('/noticeMgmt/noticeList', {
        params,
      })

      const common = response.data.common
      const data = response.data.data
      let listData = undefined
      if (common.resultCode == 'NOTICES0200') {
        listData = data.noticeList
      }

      console.log('notice', listData)
      if (Array.isArray(listData)) {
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching term list:', error)
    }
  }

  useEffect(() => {
    getNoticeList()
  }, [])

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>공지게시판 관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <FormGroup>
              <Label htmlFor={'openYn'}>공개여부</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="openYn"
                name="openYn"
                width="100px"
                onChange={handleSearch}
                value={srchData.openYn}
              >
                <option value="">전체</option>
                {getComCodes('OPEN_YN').map((item) => (
                  <option key={'openYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                width="100px"
                onChange={handleSearch}
                value={srchData.useYn}
              >
                <option value="">전체</option>
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'tpNoticeYn'}>상단고정</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="tpNoticeYn"
                name="tpNoticeYn"
                width="100px"
                onChange={handleSearch}
                value={srchData.tpNoticeYn}
              >
                <option value="">전체</option>
                {getComCodes('TP_NOTICE_YN').map((item) => (
                  <option key={'tpNoticeYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'popupYn'}>팝업</Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="popupYn"
                name="popupYn"
                width="100px"
                onChange={handleSearch}
                value={srchData.popupYn}
              >
                <option value="">전체</option>
                {getComCodes('POPUP_YN').map((item) => (
                  <option key={'popupYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'srchOpt'}>
                <></>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <FlexWrapper>
                <Select
                  id="srchOpt"
                  name="srchOpt"
                  width="100px"
                  onChange={handleSearch}
                  value={srchData.srchOpt}
                >
                  <option value="00">전체</option>
                  <option value="01">제목</option>
                  <option value="02">내용</option>
                </Select>
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'srchStr'}>
                <></>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <FlexWrapper>
                <Input
                  placeholder="제목 및 내용 검색 가능"
                  width="300px"
                  id="srchStr"
                  name="srchStr"
                  value={srchData.srchStr}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <FlexWrapper gap="5px">
                <Button
                  onClick={getNoticeList}
                  color="neverbe"
                  variant="outlined"
                >
                  검색
                </Button>
                <Button
                  onClick={() => {
                    navigate('/noticeMgmt/detail', {
                      state: {
                        srchData: srchData,
                        page: currentPage,
                        perPage: perPage,
                      },
                    })
                  }}
                  color="neverbe"
                >
                  등록
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
          <div style={{ color: 'red', marginTop: '5px', textAlign: 'right' }}>
            * 옵션 선택 후 검색어 없이 조회 가능
          </div>
        </div>
      </div>
      <DataTable
        // TODO 페이징처리는 서버 vs 클라이언트 (현행 클라이언트)
        columns={columns}
        data={data}
        customStyles={tableStyles}
        // paginationDefaultPage={currentPage}
        // onChangePage={(page) => setCurrentPage(page)}
        pagination={true}
        // paginationPerPage={perPage}
        // onChangeRowsPerPage={(page) => setPerPage(page)}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
        noDataComponent={<>조회결과가 없습니다.</>}
      />
    </>
  )
}
export default NoticeMgmt
